import React, { useState } from 'react';
import { Drawer, Select, DatePicker, Button, Space, notification, Divider } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { HITECH_API, getAuthHeaders } from './api';

const { RangePicker } = DatePicker;

const ExportDrawer = ({ open, onClose, departments }) => {
  const [status, setStatus] = useState('all');
  const [department, setDepartment] = useState('all');
  const [dateRange, setDateRange] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status !== 'all') params.set('status', status);
      if (department !== 'all') params.set('department', department);
      if (dateRange && dateRange[0]) params.set('dateFrom', dateRange[0].toISOString());
      if (dateRange && dateRange[1]) params.set('dateTo', dateRange[1].toISOString());

      const res = await fetch(`${HITECH_API}/admin/applications/export?${params}`, {
        headers: getAuthHeaders(),
      });
      const data = await res.json();

      if (!data.success || !data.applications?.length) {
        notification.warning({ message: 'No applications found for the selected filters' });
        return;
      }

      const rows = data.applications.map((a, i) => ({
        'Sr.': i + 1,
        'Applied On': a.createdAt ? new Date(a.createdAt).toLocaleDateString('en-IN') : '',
        'Name': a.name || '',
        'Gender': a.gender || '',
        'Date of Birth': a.dob ? new Date(a.dob).toLocaleDateString('en-IN') : '',
        'Contact': a.contactNumber || '',
        'Alt. Contact': a.alternateContactNumber || '',
        'Email': a.emailId || '',
        'Department': a.departmentAppliedFor || '',
        'Position Applied': a.positionAppliedFor || '',
        'Fresher': a.fresher || '',
        'Current Company': a.currentCompanyName || '',
        'Current Designation': a.currentDesignation || '',
        'Total Experience (yrs)': a.totalExperience || '',
        'Current Location': a.currentLocation || '',
        'Home Location': a.home || '',
        'Current CTC': a.currentCTC || '',
        'Expected CTC': a.expectedCTC || '',
        'Notice Period': a.noticePeriod || '',
        'Qualification': a.qualification || '',
        'Skills': a.skill || '',
        'Reference': a.reference || '',
        'Referred By': a.referenceOfFriend || '',
        'Reference (Others)': a.referenceOfOthers || '',
        'Remarks': a.remarks || '',
        'Admin Remarks': a.adminRemarks || '',
        'Status': a.status || 'pending',
        'Resume': a.resume ? 'View Resume' : '',
        'Photo': a.photo ? 'View Photo' : '',
      }));

      const ws = XLSX.utils.json_to_sheet(rows);

      // Add hyperlinks to Resume and Photo cells
      const headers = Object.keys(rows[0] || {});
      const resumeCol = headers.indexOf('Resume');
      const photoCol = headers.indexOf('Photo');
      data.applications.forEach((a, i) => {
        const rowIdx = i + 1; // row 0 is header
        if (a.resume && resumeCol >= 0) {
          const ref = XLSX.utils.encode_cell({ r: rowIdx, c: resumeCol });
          if (ws[ref]) ws[ref].l = { Target: a.resume };
        }
        if (a.photo && photoCol >= 0) {
          const ref = XLSX.utils.encode_cell({ r: rowIdx, c: photoCol });
          if (ws[ref]) ws[ref].l = { Target: a.photo };
        }
      });

      // Auto column widths
      const colWidths = Object.keys(rows[0] || {}).map(key => ({
        wch: Math.max(key.length, ...rows.map(r => String(r[key] || '').length), 12),
      }));
      ws['!cols'] = colWidths;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Applications');

      const dateStr = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `hitech-applications-${dateStr}.xlsx`);

      notification.success({ message: `Exported ${rows.length} application(s) successfully` });
      onClose();
    } catch {
      notification.error({ message: 'Export failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setStatus('all');
    setDepartment('all');
    setDateRange(null);
  };

  return (
    <Drawer
      title={<span style={{ color: '#0d2e61', fontWeight: 700 }}>Export Applications to Excel</span>}
      open={open}
      onClose={onClose}
      width={400}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={handleReset}>Reset</Button>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            loading={loading}
            onClick={handleExport}
            style={{ background: '#0d2e61', borderColor: '#0d2e61' }}
          >
            Download Excel
          </Button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div style={{ width: '100%' }}>
          <div style={styles.label}>Status</div>
          <Select value={status} onChange={setStatus} style={{ width: '100%' }} size="large">
            <Select.Option value="all">All Statuses</Select.Option>
            <Select.Option value="pending">Pending</Select.Option>
            <Select.Option value="shortlisted">Shortlisted</Select.Option>
            <Select.Option value="rejected">Rejected</Select.Option>
          </Select>
        </div>

        <div style={{ width: '100%' }}>
          <div style={styles.label}>Department</div>
          <Select value={department} onChange={setDepartment} style={{ width: '100%' }} size="large">
            <Select.Option value="all">All Departments</Select.Option>
            {departments.map(d => (
              <Select.Option key={d} value={d}>{d}</Select.Option>
            ))}
          </Select>
        </div>

        <div style={{ width: '100%' }}>
          <div style={styles.label}>Applied Date Range</div>
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            style={{ width: '100%' }}
            size="large"
            format="DD/MM/YYYY"
            placeholder={['From date', 'To date']}
            disabledDate={(current) => current && current.isAfter(new Date(), 'day')}
          />
        </div>

        <Divider style={{ margin: '4px 0' }} />
        <p style={{ color: '#888', fontSize: 13, margin: 0 }}>
          Leave filters empty to export all applications. The Excel file will include all application details including resume and photo links.
        </p>
      </div>
    </Drawer>
  );
};

const styles = {
  label: { fontWeight: 600, color: '#0d2e61', marginBottom: 6, fontSize: 13 },
};

export default ExportDrawer;
